import { useMemo, useState } from "react";
import { articles, categories } from "@/mocks/content";
import ArticleCard from "@/components/feature/ArticleCard";

export default function LatestFeed() {
  const [keyword, setKeyword] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [sort, setSort] = useState<"new" | "hot">("new");

  const list = useMemo(() => {
    let result = [...articles];
    if (activeCat !== "all") result = result.filter((a) => a.categorySlug === activeCat);
    const kw = keyword.trim();
    if (kw) {
      result = result.filter(
        (a) =>
          a.title.includes(kw) ||
          a.excerpt.includes(kw) ||
          a.tags.some((t) => t.includes(kw))
      );
    }
    result.sort((a, b) =>
      sort === "hot" ? b.views - a.views : (a.date < b.date ? 1 : -1)
    );
    return result;
  }, [keyword, activeCat, sort]);

  return (
    <section id="latest" className="w-full px-4 md:px-8 pb-4">
      <div className="bg-background-100 rounded-lg p-5 md:p-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-xs md:text-sm text-secondary-700 font-medium">持续更新</span>
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mt-1">
              最新更新内容流
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <div className="relative flex-1 sm:w-64">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索标题、摘要或标签"
                aria-label="搜索内容"
                className="w-full pl-10 pr-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />
            </div>
            <div className="flex bg-background-50 border border-background-300 rounded-lg p-1">
              {(["new", "hot"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSort(s)}
                  className={`flex-1 sm:flex-none px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    sort === s
                      ? "bg-secondary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {s === "new" ? "最新" : "最热"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <button
            type="button"
            onClick={() => setActiveCat("all")}
            className={`px-3.5 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              activeCat === "all"
                ? "bg-primary-500 text-background-50"
                : "bg-background-50 text-foreground-600 border border-background-300 hover:border-primary-300"
            }`}
          >
            全部
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => setActiveCat(c.slug)}
              className={`px-3.5 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeCat === c.slug
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 border border-background-300 hover:border-primary-300"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {list.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <span className="w-12 h-12 mx-auto flex items-center justify-center text-foreground-300">
              <i className="ri-file-search-line text-4xl" />
            </span>
            <p className="mt-3 text-sm text-foreground-500">
              没有找到匹配的内容，换个关键词试试
            </p>
          </div>
        )}
      </div>
    </section>
  );
}